import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/shared/apiService';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import { usePredictiveInsights } from '../hooks/usePredictiveInsights';

// New components
import SymbolicTimeline from '../components/patient/SymbolicTimeline';
import ForecastBlock from '../components/patient/ForecastBlock';
import WeeklyInsights from '../components/patient/WeeklyInsights';
import ReportDownload from '../components/patient/ReportDownload';
import GuidedInterventionCard from '../components/GuidedInterventionCard';

const PatientPortalContent: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { insights: predictiveData, isLoading: predictiveLoading } = usePredictiveInsights();

  // Fetch symbolic tags for the past week
  const { data: symbolicTags } = useQuery({
    queryKey: ['symbolic-tags', user?.uid],
    queryFn: () => apiService.getSymbolicTags(user?.uid || ''),
    enabled: !!user?.uid,
  });

  // Fetch weekly report if available
  const { data: reports } = useQuery({
    queryKey: ['user-reports', user?.uid],
    queryFn: () => apiService.listUserReports(5),
    enabled: !!user?.uid,
  });

  const latestReport = reports?.[0];

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6 px-6 pt-6">
        <h1 className="text-3xl font-bold">My Mentra</h1>
        {user?.displayName && (
          <p className="text-xl text-gray-600">
            Welcome back, {user.displayName}
          </p>
        )}
      </div>


      {/* Symbolic Timeline */}
      <PageSection title="Your Emotional Journey" emoji="📈">
        <SymbolicTimeline userId={user?.uid || ''} />
      </PageSection>

      {/* Forecast Block */}
      <PageSection title="AI Mood Forecast" emoji="🔮">
        <ForecastBlock 
          predictiveData={predictiveData} 
          isLoading={predictiveLoading} 
        />
      </PageSection>

      {/* Weekly Insights */}
      <PageSection title="This Week's Insights" emoji="💡">
        <WeeklyInsights 
          symbolicTags={symbolicTags} 
          userId={user?.uid || ''} 
        />
      </PageSection>

      {/* Report Download */}
      <PageSection title="Your Reports" emoji="📋">
        <ReportDownload 
          latestReport={latestReport}
          userId={user?.uid || ''}
        />
      </PageSection>

      {/* Guided Intervention Card */}
      <PageSection title="Guided Intervention" emoji="💡">
        <GuidedInterventionCard />
      </PageSection>

      {/* Quick Actions */}
      <PageSection title="Quick Actions" emoji="⚡">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/chat')}
            className="p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <div className="text-center">
              <div className="text-2xl mb-2">💬</div>
              <div className="font-medium">Start Chat</div>
              <div className="text-sm opacity-90">Talk with your AI companion</div>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-4 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            <div className="text-center">
              <div className="text-2xl mb-2">📊</div>
              <div className="font-medium">View Dashboard</div>
              <div className="text-sm opacity-90">See detailed analytics</div>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/patient-settings')}
            className="p-4 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
          >
            <div className="text-center">
              <div className="text-2xl mb-2">⚙️</div>
              <div className="font-medium">Settings</div>
              <div className="text-sm opacity-90">Manage your preferences</div>
            </div>
          </button>
        </div>
      </PageSection>
    </PageContainer>
  );
};

const PatientPortal: React.FC = () => {
  return <PatientPortalContent />;
};

export default PatientPortal; 